export interface IPrimaryKey {
  id: string;
}

export interface IAuditable {
  action: string;
  actionBy: string;
  actionOn: Date;
}

export interface IAddress extends IAuditable, IPrimaryKey {
  streets: IAddressStreet[];
  city?: string;
  stateCode?: string;
  postalCode?: string;
  countryCode?: string;
  privateUserId?: string;
  isPrivate: boolean;
  isActive: boolean;
}

export interface IAddressStreet {
  key: number;
  value: string;
}

export interface IAppUserSettings {
  userSettingsJson: string;
}
export interface IAppUser extends IAuditable, IPrimaryKey {
  userId: string;
  displayName: string;
  email: string;
  isActive: boolean;
  userSettingsJson: string;
}
export interface IUserSettings {
  'Default Site'?: string;
  useCommonName?: boolean;
  useSiteCode?: boolean;
}
export interface ICatalog extends IAuditable, IPrimaryKey {
  locationId: string;
  siteCode: string;
  leadTime?: number;
  leadTimeMessage?: string;

  containers: ICatalogContainer[];
  insects: ICatalogInsect[];
  substrates: ICatalogSubstrate[];
  projects: ICatalogProject[];
  workflowNotifications: ICatalogWorkflowNotification[];
  bartenderConfig?: BartenderConfiguration;
}

export interface ICatalogContainer extends IAuditable, IPrimaryKey {
  catalogId: string;
  containerId: string;
  isAvailable: boolean;
  isActive: boolean;
  container: IContainer;
  standardVolumeUom: any;
  standardVolume: any;
}

export interface IContainerInsectGrowthStageUpdateRequest {
  id: string;
  infestationRate?: number;
  isAvailable: boolean;
  actionBy: string;
}

export interface IContainerSubstrateUpdateRequest {
  id: string;
  standardVolume?: number;
  standardVolumeUom?: string;
  isAvailable: boolean;
  actionBy: string;
}

export interface ICatalogContainerInsectGrowthStageView extends IAuditable, IPrimaryKey {
  catalogId: string;
  containerId: string;
  infestationRate?: number;
  isAvailable: boolean;
  isActive: boolean;
  insectId: string;
  scientificName: string;
  growthStageId: string;
  growthStageName: string;
  growthStageSortOrder?: number;
}

export interface ICatalogOrderItemTypeUpdateRequest extends IAuditable, IPrimaryKey {
  id: string;
  leadTime: number;
  hasRecurringOrders: boolean;
  maxRecurrenceDuration: number;
  isActive: boolean;
  actionBy: string;
}

export interface ICatalogContainerSubstrateView extends IAuditable, IPrimaryKey {
  catalogId: string;
  containerId: string;
  catalogSubstrateId: string;
  standardVolume?: number;
  standardVolumeUom?: string;
  isAvailable: boolean;
  isActive: boolean;
  substrateId: string;
  substrateName: string;
}

export interface ICatalogInsect extends IAuditable, IPrimaryKey {
  catalogId: string;
  insectId: string;
  isAvailable: boolean;
  isExternal: boolean;
  leadTime?: number;
  isActive: boolean;

  insect: IInsect;
  growthStages: ICatalogInsectGrowthStage[];
  variants: ICatalogInsectVariant[];
  insectSynonymId?: string;
}

export interface ICatalogContainerAddRequest {
  catalogId: string;
  containerId: string;
  isAvailable: boolean;
  isActive: boolean;
  actionBy: string;
}

export interface ICatalogContainerUpdateRequest {
  id: string;
  isAvailable: boolean;
  isActive: boolean;
  actionBy: string;
  standardVolume: number;
  standardVolumeUom: string;
}

export interface ICatalogProjectUpdateRequest {
  id: string;
  isAvailable: boolean;
  isActive: boolean;
  actionBy: string;
  insectSynonymId?: string;
}

export interface ICatalogProject {
  catalogId: string;
  containerId: string;
  id: string;
  isAvailable: boolean;
  isActive: boolean;
  actionBy: string;
  actionOn: Date;
  action: string;
  project: IProject;
}

export interface ICatalogInsectGrowthStage extends IAuditable, IPrimaryKey {
  catalogInsectId: string;
  insectGrowthStageId: string;
  isAvailable: boolean;
  isExternal: boolean;
  leadTime?: number;
  isActive: boolean;

  insectGrowthStage: IInsectGrowthStage;
}

export interface ICatalogInsectGrowthStageUpdateRequest {
  id: string;
  leadTime?: number;
  isAvailable: boolean;
  isExternal: boolean;
  isActive: boolean;
  actionBy: string;
}

export interface ICatalogInsectVariant extends IAuditable, IPrimaryKey {
  catalogInsectId: string;
  variantTypeId: string;
  siteCode?: string | null;
  isAvailable: boolean;
  isExternal: boolean;
  leadTime?: number;
  isActive: boolean;

  insectVariant: IInsectVariant;
}

export interface ICatalogInsectVariantUpdateRequest {
  id: string;
  leadTime?: number;
  siteCode?: string | null;
  isAvailable: boolean;
  isExternal: boolean;
  isActive: boolean;
  actionBy: string;
}

export interface ICatalogSubstrate extends IAuditable, IPrimaryKey {
  catalogId: string;
  substrateId: string;
  leadTime?: number;
  isAvailable: boolean;
  isExternal: boolean;
  isActive: boolean;
  recipeJson: string;
  substrate: ISubstrate;
}

export interface ICatalogSubstrateAddRequest {
  catalogId: string;
  substrateId: string;
  leadTime?: number;
  isAvailable: boolean;
  isExternal: boolean;
  isActive: boolean;
  actionBy: string;
}

export interface ICatalogSubstrateUpdateRequest {
  id: string;
  leadTime?: number;
  isAvailable: boolean;
  isExternal: boolean;
  isActive: boolean;
  actionBy: string;
}

export interface ICatalogUpdateRequest extends IPrimaryKey {
  id: string;
  locationId: string;
  siteCode: string;
  BartenderConfig?: BartenderConfiguration;
  leadTime?: number;
  leadTimeMessage?: string;
  actionBy: string;
}
export interface ICatalogInsectUpdateRequest {
  id: string;
  insectSynonymId?: string | null;
  isAvailable?: boolean;
  isExternal?: boolean;
  leadTime?: number | null;
  isActive?: boolean;
  actionBy: string;
}

export interface ICatalogWorkflowNotification extends IAuditable, IPrimaryKey {
  catalogId: string;
  workflowStatusCodeId: string;

  toList: string[];
  ccList: string[];
  subjectTemplate: string;
  bodyHeaderText?: string;
  bodyTemplate?: string;
  bodyFooterText?: string;

  isActive: boolean;
  workflowStatusCode: IWorkflowStatusCode;
}

export interface ICatalogWorkflowNotificationUpdateRequest extends IPrimaryKey {
  toList: string[];
  ccList: string[];
  subjectTemplate: string;
  bodyHeaderText?: string;
  bodyTemplate?: string;
  bodyFooterText?: string;
  isActive: boolean;
  actionBy: string;
}

export interface IContainer extends IAuditable, IPrimaryKey {
  name: string;
  containerTypeId: string;
  containerType: IContainerType;
  isActive: boolean;
}

export interface IContainerType extends IAuditable, IPrimaryKey {
  name: string;
  isActive: boolean;
}

export interface IPermits extends IAuditable, IPrimaryKey {
  name: string;
  isActive: boolean;
  uploadId?: string;
  fileName?: string;
}

export interface IContainmentType extends IAuditable, IPrimaryKey {
  name: string;
  isActive: boolean;
}

export interface IDeliveryUnavailableDate extends IAuditable, IPrimaryKey {
  date: any;
  isActive: boolean;
}

export interface IDeliveryDay extends IAuditable, IPrimaryKey {
  dayId: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  day: 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';
  isActive: boolean;
}

export interface IDictionaryEntry {
  id: string;
  text: string;
}

export interface IEmailRequest {
  to: string[];
  cc: string[];
  subject: string;
  message: string;
}

export interface IEppoCode {
  eppocode?: string;
  type?: string;
  fullname?: string;
  nameType?: string;
  lang?: string;
}

export interface IFeedbackAnswer {
  answerText: string | undefined;
  questionId: string;
}

export interface IFeedbackAnswerRequest {
  entityType: string;
  entityIds: string[];
  answers: IFeedbackAnswer[];
}

export interface IGrowthStage extends IAuditable, IPrimaryKey {
  name: string;
  isActive: boolean;
  isDefault: boolean;
  sortOrder?: number;
}
export interface IGrowthStageSortOrderItem {
  id: string;
  sortOrder: number;
}

export interface IGrowthStageMappedGetRequest {
  catalogId: string;
  catalogInsectGrowthStageId?: string | any;
  catalogInsectId: string;
  insectGrowthStageId?: string | any;
  insectId: string;
  growthStageName?: string;
  leadTime?: number;
  growthStage?: IGrowthStage;
  catalogContainerInsectGrowthStageId?: string;
  containerId?: string;
  containerName?: string;
  containerTypeName?: string;
  infestationRate?: number;
  isAvailable?: boolean;
  catalogInsectVariantId?: string;
  insectVariantId?: string;
  variantLeadTime?: number;
  siteCode?: string;
  variantName?: string;
  variantTypeName?: string;
}

export interface IInsect extends IAuditable, IPrimaryKey {
  scientificName: string;
  taxonJson: string;
  eppoCode?: string;
  eppoJson?: string;
  isActive: boolean;
}

export interface IInsectGrowthStage extends IAuditable, IPrimaryKey {
  insectId: string;
  growthStageId: string;
  degreeDays?: number;
  growthStage: IGrowthStage;
  isActive: boolean;
}

export interface IInsectVariant extends IAuditable, IPrimaryKey {
  insectId: string;
  variantTypeId: string;
  description: string;
  isActive: boolean;
  variantType: IVariantType;
}

export interface ILocation extends IAuditable, IPrimaryKey {
  name: string;
  parentId: string;
  locationTypeId: string;
  containmentTypeId: string;
  address?: IAddress | null;
  isSelectable: boolean;
  isActive: boolean;
  children: ILocation[];
}

export interface ILocationAddRequest extends IAuditable {
  name: string;
  parentId: string;
  locationTypeId: string;
  containmentTypeId: string;
  address?: IAddress | null;
  isSelectable: boolean;
  isActive: boolean;
}

export interface ILocationUpdateRequest extends ILocationAddRequest, IPrimaryKey {}

export interface IInsect extends IAuditable, IPrimaryKey {
  scientificName: string;
  taxonJson: string;
  eppoCode?: string;
  eppoJson?: string;
  isActive: boolean;
  insectSynonyms: IInsectSynonym[];
  insectVariants: IInsectVariant[];
  insectGrowthStages: IInsectGrowthStage[];
}

export interface IInsectSynonym extends IAuditable, IPrimaryKey {
  name: string;
  isActive: boolean;
  insectId: string;
}

export interface IInsectGrowthStage extends IAuditable, IPrimaryKey {
  insectId: string;
  growthStageId: string;
  degreeDays?: number;
  uploadId?: string;
  imageUrl?: string;
}

export interface IInsectVariant extends IAuditable, IPrimaryKey {
  insectId: string;
  variantTypeId: string;
  description: string;
  isActive: boolean;
}

export interface IInsectAddRequest extends IAuditable {
  scientificName: string;
  taxonJson: string;
  eppoCode: string;
  eppoJson: string;
  isActive: boolean;
  insectSynonyms: IInsectSynonymAddRequest[];
  insectVariants: IInsectVariantAddRequest[];
  insectGrowthStages: IInsectGrowthStageAddRequest[];
}

export interface IInsectSynonymAddRequest extends IAuditable {
  name: string;
  isActive: boolean;
}

export interface IInsectVariantAddRequest extends IAuditable {
  variantTypeId: string;
  description: string;
  isActive: boolean;
  insectVariantCodes: IInsectVariantCodeAddRequest[];
}

export interface IInsectVariantCodeAddRequest extends IAuditable {
  isActive: boolean;
  code: string;
}

export interface IInsectGrowthStageAddRequest extends IAuditable {
  growthStageId: string;
  degreeDays?: number;
  uploadId?: string;
}

export interface ILocationUpdateRequest extends ILocationAddRequest, IPrimaryKey {}
export interface IInsectUpdateRequest extends IInsectAddRequest, IPrimaryKey {}
export interface IInsectSynonymUpdateRequest extends IInsectSynonymAddRequest, IPrimaryKey {}
export interface IInsectVariantUpdateRequest extends IInsectVariantAddRequest, IPrimaryKey {}
export interface IInsectGrowthStageUpdateRequest extends IInsectGrowthStageAddRequest, IPrimaryKey {}

export interface ILocationType extends IAuditable, IPrimaryKey {
  name: string;
  isActive: boolean;
}

export interface IProject extends IAuditable, IPrimaryKey {
  name: string;
  isActive: boolean;
}

export interface ISurveyQuestionOption {
  text: string;
  order: number;
}

export interface ISurveyQuestion extends IAuditable, IPrimaryKey {
  text: string;
  type: string;
  order: number;
  category: string;
  options: ISurveyQuestionOption[];
}

export interface ITaxonResponse {
  data: ITaxon[];
  pagination: ITaxonPagination;
}

export interface ITaxon {
  id?: number;
  parentId?: number;
  division?: string | undefined;
  rank?: string | undefined;
  unPublishedOrganisms?: boolean;
  names?: ITaxonName[] | undefined;
  deleted?: boolean;
  lastUpdated?: Date;
}

export interface ITaxonName {
  nameText?: string | undefined;
  uniqueName?: string | undefined;
  nameClass?: string | undefined;
  isPartialName?: boolean;
}

export interface ITaxonPagination {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPageCount: number;
  nextPageExists: boolean;
  previousPageExists: boolean;
}

export interface IUnitOfMeasure extends IAuditable, IPrimaryKey {
  name: string;
  conversionFactor: number;
  isActive: boolean;
}

export interface IVariantType extends IAuditable, IPrimaryKey {
  name: string;
  isActive: boolean;
  isDefault: boolean;
}

export interface IVendor extends IAuditable, IPrimaryKey {
  name: string;
  isActive: boolean;
}

export interface ISubstrate extends IAuditable, IPrimaryKey {
  name: string;
  isActive: boolean;
}

export interface IRole extends IAuditable, IPrimaryKey {
  name: string;
  description: string;
  rolePermissions: IRolePermission[];
}

export interface IPermission extends IAuditable, IPrimaryKey {
  name: string;
  description: string;
}

export interface IRolePermission extends IAuditable, IPrimaryKey {
  roleId: string;
  permissionId: string;
  permission: IPermission;
}

export interface IRolePermissionUpdateRequest {
  roleId: string;
  permissions: string[];
  actionBy: string;
}

export interface ICatalogOrderItemType extends IAuditable, IPrimaryKey {
  catalogId: string;
  orderItemTypeId: string;
  leadTime: number;
  hasRecurringOrders: boolean;
  maxRecurrenceDuration: number;
  isActive: boolean;
  actionBy: string;
}

export interface IOrderItemType extends IAuditable, IPrimaryKey {
  catalogId: string;
  orderItemTypeId: string;
  hasRecurringOrders: boolean;
  maxRecurrenceDuration: number;
  isActive: boolean;
  actionBy: string;
}

export interface IOrderItemInstance extends IAuditable, IPrimaryKey {
  orderItemInstanceSequence: number;
  orderId: string;
  orderItemId: string;
  deliveryDate: Date;
  note?: string;

  catalogInsectVariantId?: string;
  catalogContainerInsectGrowthStageId?: string;
  infestationRateQuantity?: number;
  insectQuantity?: number;
  insectPermitId?: string;

  catalogContainerSubstrateId?: string;
  substrateAmount?: number;
  substrateAmountUom?: string;
  substrateQuantity?: number;

  actionBy: string;
}

export interface IWorkflowStatusCode extends IAuditable, IPrimaryKey {
  name: string;
  orderEntityType: string;
  isActive: boolean;
}

export interface IOrder extends IAuditable, IPrimaryKey {
  id: string;
  catalogId: string;
  deliveryLocationId: string;
  orderYear: string;
  orderItems: IOrderItem[];
  orderSiteCode: string;
  notificationJson: string;
  alternateOwnerJson: string;
  orderName: string;
  requester: string;
  orderSequence: number;
}

export interface IOrderAddRequest {
  catalogId: string;
  deliveryLocationId: string;
  orderYear: string;
  requester: string;
  notificationJson: string;
  alternateOwnerJson: string;
  orderName: string;
}

export interface IOrderUpdateRequest {
  id: string;
  deliveryLocationId: string;
  orderName: string;
  notificationJson: string;
  alternateOwnerJson: string;
}

export interface IOrderItem extends IAuditable, IPrimaryKey {
  id: string;
  catalogId: string;
  orderItemTypeId: string;
  hasRecurringOrders: boolean;
  maxRecurrenceDuration: number;
  isActive: boolean;
  actionBy: string;
}

export interface IOrderItemAddRequest {
  orderId: string;
  orderItemTypeId: string;
  projectId?: string;
  firstDeliveryDate: string;
  note?: string;
  actionBy?: string;
  catalogInsectVariantId?: string;
  catalogContainerInsectGrowthStageId?: string;
  insectQuantity?: number;
  insectPermitId?: string;
  infestationRateQuantity?: number;
  catalogContainerSubstrateId?: string;
  substrateAmount?: number;
  substrateAmountUom?: string;
  substrateQuantity?: number;
  recurrenceSchedule?: IRecurrenceSchedule;
  instances: IOrderItemInstanceAddRequest[];
  standardFillAmount: boolean;
  standardInfestationRate: boolean;
}

export interface IOrderItemInstanceAddRequest {
  deliveryDate: string;
  note?: string;
  catalogInsectVariantId?: string;
  catalogContainerInsectGrowthStageId?: string;
  insectQuantity?: number;
  insectPermitId?: string;
  infestationRateQuantity?: number;
  catalogContainerSubstrateId?: string;
  substrateAmount?: number;
  substrateAmountUom?: string;
  substrateQuantity?: number;
  standardFillAmount: boolean;
  standardInfestationRate: boolean;
  vendorDelay?: boolean;
  delviveryLocationId?: string | null;
}

export interface IOrderItemInstanceUpdateRequest {
  id: string;
  orderItemInstanceSequence: number;
  orderId: string;
  orderItemId: string;
  deliveryDate: string;
  note?: string;
  catalogInsectVariantId?: string;
  catalogContainerInsectGrowthStageId?: string;
  insectQuantity?: number;
  insectPermitId?: string;
  infestationRateQuantity?: number;
  catalogContainerSubstrateId?: string;
  substrateAmount?: number;
  substrateAmountUom?: string;
  substrateQuantity?: number;
  standardFillAmount: boolean;
  standardInfestationRate: boolean;
  lastEditableDate?: string;
  deliveryLocationId?: string | null;
}

export interface IOrderItemInstanceUpdateDeliveryLocationRequest {
  instances: string[];
  deliveryLocation: string;
}

export interface IOrderItemInstanceUpdateShippingRequest {
  instances: string[];
  shippingVendor: string | null;
  shippingTrackingNumber: string | null;
  shippingDate: string | null;
}

export interface IOrderItemInstanceUpdateStatusRequest {
  instances: string[];
  status: string;
}

export interface IOrderItemInstanceAddVendorNameAndSitePermitRequest {
  instances: string[];
  vendorId: string | null;
  sitePermitId: string | null;
}

export interface IOrderItemInstanceAddVendorDelayInfoRequest {
  instances: string[];
  vendorDelay: boolean | null;
  vendorDelayNote: string | null;
}

export interface IOrderItemInstanceAddVendorDeliveryDateRequest {
  instances: string[];
  vendorDeliveryDate: Date | null;
}

export interface IOrderItemUpdateRequest {
  id: string;
  orderItemTypeId: string;
  projectId?: string;
  firstDeliveryDate: string;
  note?: string;
  actionBy?: string;
  catalogInsectVariantId?: string;
  catalogContainerInsectGrowthStageId?: string;
  insectQuantity?: number;
  insectPermitId?: string;
  infestationRateQuantity?: number;
  catalogContainerSubstrateId?: string;
  substrateAmount?: number;
  substrateAmountUom?: string;
  substrateQuantity?: number;
  recurrenceSchedule?: IRecurrenceSchedule;
  standardFillAmount: boolean;
  standardInfestationRate: boolean;
  deliveryLocationId?: string | null;
  instances: IOrderItemInstanceAddRequest[];
}

export interface IRecurrenceSchedule {
  firstDeliveryDate: string;
  scheduleType: string;
  configuration?: IRecurrenceConfiguration;
}

export interface IRecurrenceConfiguration {
  repeatEvery?: number;
  repeatUnit?: string;
  daysOfWeek?: string[];
  endDate?: string;
  customDates?: string[];
}

export interface ICountResult {
  totalCount: number;
}

export interface ISearchRequest {
  columns?: string[];
  filters: ISearchRequestFilter[];
  sorts: ISearchSort[];
  skip?: number;
  take?: number;
}

export interface ISearchRequestFilter {
  fieldname: string;
  logic: 'and' | 'or';
  values: ISearchFilterValue[];
}
export interface ISearchFilterValue {
  operator: string;
  value?: any;
}

export interface ISearchResult {
  results: string;
  chunkCount: number;
  chunkIndex: number;
  chunkSize: number;
  startIndex: number;
  endIndex: number;
  totalCount: number;
}

export interface IOrderItemSearchRequest {
  filters: ISearchFilter[];
  orderId?: string;
  orderItemId?: string;
  requester?: string;
  sorts: ISearchSort[];
  skip?: number;
  take?: number;
}

export interface IOrderApprovalSearchRequest {
  filters: ISearchFilter[];
  requester?: string;
  sorts: ISearchSort[];
  skip?: number;
  take?: number;
}

export interface ISearchFilter {
  name: string;
  operator: string;
  value?: any;
}

export interface ISearchSort {
  name: string;
  direction: string;
}

export interface IPrinterOption {
  label: string;
  value: string;
}

export interface ILabelTemplateOption {
  label: string;
  value: string;
  libraryId: string;
  relativePath: string;
}

export interface IPrinters {
  serverPrinters: string[];
  remotePrinters: string[];
}

export interface ILibrary {
  id: string;
  name: string;
  description: string;
  order: number;
  fileFilter: string;
  relativePath: string;
  validVisibleUsers: any[];
}

export interface ILibraryContents {
  library: ILibrary;
  contents: string[];
}

export interface ILabelTemplate {
  name: string;
  libraryId: string;
  libraryName: string;
  relativePath: string;
}

export interface IPrintRequest {
  libraryID: string;
  relativePath: string;
  absolutePath?: string;
  documentPassword?: string;
  printRequestID: string;
  printer: string;
  startingPosition?: number;
  copies: number;
  serialNumbers?: number;
  namedDataSources?: { [key: string]: string };
  dataEntryControls?: { [key: string]: string };
}

export interface IPrintResult {
  success: boolean;
  message: string;
  printRequestID: string;
  responseJson?: string;
}

export const Roles = {
  Admin: 'InsectaOrderAdmin',
  Fulfillment: 'InsectaOrderFulfillment',
  Requester: 'InsectaOrderRequester',
  SiteManager: 'InsectaOrderSiteManager'
} as const;

export const OrderStatus = {
  Approved: 'Approved',
  Cancelled: 'Cancelled',
  Completed: 'Completed',
  Deleted: 'Deleted',
  Denied: 'Denied',
  Draft: 'Draft',
  InProgress: 'In Progress',
  Submitted: 'Submitted'
} as const;

export const OrderItemStatus = {
  Approved: 'Approved',
  Cancelled: 'Cancelled',
  Completed: 'Completed',
  Deleted: 'Deleted',
  Denied: 'Denied',
  Draft: 'Draft',
  InProgress: 'In Progress',
  Submitted: 'Submitted'
} as const;

export const OrderItemInstanceStatus = {
  Approved: 'Approved',
  Cancelled: 'Cancelled',
  Completed: 'Completed',
  Delayed: 'Delayed',
  Deleted: 'Deleted',
  Delivered: 'Delivered',
  Denied: 'Denied',
  Draft: 'Draft',
  Submitted: 'Submitted',
  Updated: 'Updated'
} as const;

export interface BartenderConfiguration {
  serverUrl: string;
  libraryId: string;
  printers: string[];
  labels: string[];
}
